'use client'

import { delay, oneSecondInMs, withTimeout } from '@repo/utils'
import type { ActionFlowResult } from '@repo/web3'

import { safeSentryBreadcrumb } from '@/helpers/sentry'
import { logger } from '@/lib/observability/logger'
import { orpc } from '@/lib/orpc/client'

const API_SYNC_INITIAL_DELAY_MS = 2 * oneSecondInMs
const API_SYNC_POLL_INTERVAL_MS = oneSecondInMs
const API_SYNC_TIMEOUT_MS = 10 * oneSecondInMs

type WaitForApiSyncDeps = {
  readIndexedHead?: (chainId: number) => Promise<string | null>
  initialDelayMs?: number
  pollIntervalMs?: number
  timeoutMs?: number
}

type SyncOutcome = 'synced' | 'timed_out'
type SyncWaitResult = { outcome: SyncOutcome; firstIndexedBlock: string | null }

export function waitForApiSync(
  callback?: (result: ActionFlowResult) => void | Promise<void>,
  deps: WaitForApiSyncDeps = {}
): (result: ActionFlowResult) => Promise<void> {
  const readIndexedHead =
    deps.readIndexedHead ??
    (async (chainId: number) => (await orpc.shared.sync.call({ chainId })).lastIndexedBlock)
  const initialDelayMs = deps.initialDelayMs ?? API_SYNC_INITIAL_DELAY_MS
  const pollIntervalMs = deps.pollIntervalMs ?? API_SYNC_POLL_INTERVAL_MS
  const timeoutMs = deps.timeoutMs ?? API_SYNC_TIMEOUT_MS

  return async (result: ActionFlowResult) => {
    const startedAt = Date.now()
    const waitResult = await waitForIndexerSync(result, {
      readIndexedHead,
      initialDelayMs,
      pollIntervalMs,
      timeoutMs
    })
    const firstHead = waitResult.firstIndexedBlock
    const firstLag =
      firstHead === null ? null : (result.receipt.blockNumber - BigInt(firstHead)).toString()

    const data = {
      chainId: result.chainId,
      outcome: waitResult.outcome,
      heads: { core: firstHead },
      lagByServiceInBlocks: { core: firstLag },
      elapsedMs: Date.now() - startedAt,
      deadlineMs: timeoutMs
    }

    safeSentryBreadcrumb({
      category: 'transaction',
      message: 'ActionFlow API sync',
      level: waitResult.outcome === 'synced' ? 'info' : 'warning',
      data
    })

    const logContext = { chainId: result.chainId, extra: data }
    if (waitResult.outcome === 'synced') {
      logger.info('ActionFlow API sync', logContext)
    } else {
      logger.warn('ActionFlow API sync', logContext)
    }

    await callback?.(result)
  }
}

async function waitForIndexerSync(
  result: ActionFlowResult,
  { readIndexedHead, initialDelayMs, pollIntervalMs, timeoutMs }: Required<WaitForApiSyncDeps>
): Promise<SyncWaitResult> {
  const deadlineMs = Date.now() + timeoutMs
  let firstIndexedBlock: string | null | undefined
  await delay(Math.min(initialDelayMs, timeoutMs))

  while (Date.now() < deadlineMs) {
    const head = await readHeadWithinDeadline(readIndexedHead, result.chainId, deadlineMs).catch(
      () => null
    )
    if (firstIndexedBlock === undefined) {
      firstIndexedBlock = head
    }
    if (head !== null && BigInt(head) >= result.receipt.blockNumber) {
      return { outcome: 'synced', firstIndexedBlock: firstIndexedBlock ?? null }
    }

    const remainingMs = deadlineMs - Date.now()
    if (remainingMs <= 0) break
    await delay(Math.min(pollIntervalMs, remainingMs))
  }

  return { outcome: 'timed_out', firstIndexedBlock: firstIndexedBlock ?? null }
}

async function readHeadWithinDeadline(
  readIndexedHead: (chainId: number) => Promise<string | null>,
  chainId: number,
  deadlineMs: number
): Promise<string | null> {
  const remainingMs = deadlineMs - Date.now()
  if (remainingMs <= 0) return null

  return withTimeout(readIndexedHead(chainId), remainingMs)
}
